import { Phone, MessageCircle, Navigation } from "lucide-react";
import { toast } from "sonner";
import { PHONE_NUMBER, WHATSAPP_NUMBER, MAPS_DIR_URL } from "@/data/clinic";

export const callClinic = () => {
  if (PHONE_NUMBER) {
    window.location.href = `tel:${PHONE_NUMBER}`;
  } else {
    toast.info("Our phone line is being connected — the number will appear here very soon. Meanwhile, walk-ins are welcome.");
  }
};

export const whatsappClinic = () => {
  if (WHATSAPP_NUMBER) {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}`, "_blank");
  } else {
    toast.info("Our WhatsApp number will be shared shortly. Meanwhile, feel free to visit the clinic — patients are seen first-come, first-served.");
  }
};

export const openDirections = () => window.open(MAPS_DIR_URL, "_blank");

const styles = {
  solid:
    "inline-flex items-center gap-2 rounded-full bg-[#14B8A6] px-6 py-3 text-sm font-semibold text-[#063A2F] shadow-sm transition-colors duration-200 hover:bg-[#0F574E] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:ring-offset-2",
  outline:
    "inline-flex items-center gap-2 rounded-full border border-[#14B8A6] px-6 py-3 text-sm font-semibold text-[#0F574E] transition-colors duration-200 hover:bg-[#14B8A6] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:ring-offset-2",
  ghost:
    "inline-flex items-center gap-2 rounded-full bg-[#063A2F] px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#0F574E] focus:outline-none focus:ring-2 focus:ring-[#063A2F] focus:ring-offset-2",
  heroOutline:
    "inline-flex items-center gap-2 rounded-full border border-[#2DD4BF]/60 px-6 py-3 text-sm font-semibold text-[#5EEAD4] transition-colors duration-200 hover:bg-[#14B8A6] hover:border-[#14B8A6] hover:text-[#063A2F] focus:outline-none focus:ring-2 focus:ring-[#2DD4BF] focus:ring-offset-2 focus:ring-offset-[#063A2F]",
  heroEmerald:
    "inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#0F574E] px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#14B8A6] hover:text-[#063A2F] focus:outline-none focus:ring-2 focus:ring-[#2DD4BF] focus:ring-offset-2 focus:ring-offset-[#063A2F]",
  solidDark:
    "inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#063A2F] transition-colors duration-200 hover:bg-[#F2FBF7] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#063A2F]",
  outlineDark:
    "inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:border-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#063A2F]",
};

export const CTAButtons = ({ dark = false, hero = false }) => {
  const pick = dark
    ? [styles.solidDark, styles.outlineDark, styles.outlineDark]
    : hero
      ? [styles.solid, styles.heroOutline, styles.heroEmerald]
      : [styles.solid, styles.outline, styles.ghost];
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="cta-buttons">
      <button data-testid="call-clinic-btn" onClick={callClinic} className={pick[0]}>
        <Phone className="h-4 w-4" strokeWidth={1.5} /> Call Clinic
      </button>
      <button data-testid="whatsapp-clinic-btn" onClick={whatsappClinic} className={pick[1]}>
        <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> WhatsApp Clinic
      </button>
      <button data-testid="get-directions-btn" onClick={openDirections} className={pick[2]}>
        <Navigation className="h-4 w-4" strokeWidth={1.5} /> Get Directions
      </button>
    </div>
  );
};

export const FloatingCTA = () => (
  <>
    {/* Mobile: compact icon-only stack, minimal footprint */}
    <div
      className="fixed bottom-5 right-5 z-40 flex flex-col gap-2.5 sm:hidden"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
    >
      <button
        data-testid="floating-call-btn"
        onClick={callClinic}
        aria-label="Call Clinic"
        className="grid h-10 w-10 place-items-center rounded-full bg-[#14B8A6] text-[#063A2F] shadow-lg transition-colors duration-200 hover:bg-[#0F574E] hover:text-white"
      >
        <Phone className="h-4 w-4" strokeWidth={1.5} />
      </button>
      <button
        data-testid="floating-whatsapp-btn"
        onClick={whatsappClinic}
        aria-label="WhatsApp Clinic"
        className="grid h-10 w-10 place-items-center rounded-full border border-[#14B8A6]/40 bg-[#FDFCF8] text-[#0F574E] shadow-lg transition-colors duration-200 hover:bg-[#F2FBF7]"
      >
        <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
      </button>
    </div>

    {/* Desktop: full labelled pill */}
    <div className="fixed bottom-6 right-6 z-40 hidden sm:flex">
      <div className="flex items-center gap-2 rounded-full border border-[#063A2F]/10 bg-[#FDFCF8]/95 p-2 shadow-lg backdrop-blur">
        <button
          data-testid="floating-call-btn-desktop"
          onClick={callClinic}
          className="inline-flex items-center gap-2 rounded-full bg-[#14B8A6] px-4 py-2 text-xs font-semibold text-[#063A2F] transition-colors duration-200 hover:bg-[#0F574E] hover:text-white"
        >
          <Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> Call
        </button>
        <button
          data-testid="floating-whatsapp-btn-desktop"
          onClick={whatsappClinic}
          className="inline-flex items-center gap-2 rounded-full border border-[#14B8A6]/40 px-4 py-2 text-xs font-semibold text-[#0F574E] transition-colors duration-200 hover:bg-[#F2FBF7]"
        >
          <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> WhatsApp
        </button>
      </div>
    </div>
  </>
);
